// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';

export type TaskOptionKey = 'rename' | 'move' | 'duplicate' | 'delete';

export interface TaskOptionMenuProps {
  readonly visible: boolean;
  readonly onSelect: (key: TaskOptionKey) => void;
  readonly onClose: () => void;
}

const ORDER: readonly TaskOptionKey[] = ['rename', 'move', 'duplicate', 'delete'];

/** Dropdown options for a single task row. */
export const TaskOptionMenu: React.FC<TaskOptionMenuProps> = ({ visible, onSelect, onClose }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={[styles.scrim, { backgroundColor: theme.colors.overlay.scrim }]}
        onPress={onClose}
      >
        <View
          style={[
            styles.menu,
            {
              backgroundColor: theme.colors.surface.primary,
              borderColor: theme.colors.border.secondary,
            },
          ]}
        >
          {ORDER.map((key) => (
            <Pressable
              key={key}
              onPress={() => {
                onSelect(key);
                onClose();
              }}
              accessibilityRole="button"
              accessibilityLabel={tasksStrings.optionMenu[key]}
              style={({ pressed }) => [
                styles.item,
                pressed && { backgroundColor: theme.colors.surface.tertiary },
              ]}
            >
              <Text
                style={[
                  styles.itemText,
                  {
                    color: key === 'delete' ? theme.colors.accent.rose : theme.colors.text.primary,
                  },
                ]}
              >
                {tasksStrings.optionMenu[key]}
              </Text>
            </Pressable>
          ))}
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  menu: {
    minWidth: 220,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  } satisfies ViewStyle,
  item: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  } satisfies ViewStyle,
  itemText: {
    fontSize: 14,
    fontWeight: '500',
  } satisfies TextStyle,
});
